import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { LOGO, IMG } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Compass, Palette, Activity, Search, HeartHandshake, Loader2, ChevronRight, MailCheck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const AGES = [["2-4", "Age 2–4"], ["5-7", "Age 5–7"], ["8-11", "Age 8–11"], ["multiple", "More than one child"]];
const INTERESTS = [
  ["afterschool", "After-school program"], ["pickup", "School pickup"], ["dropoff", "Drop-off service"],
  ["camps", "Holiday camps"], ["weekend", "Weekend activities"], ["birthday", "Birthday experiences"],
  ["membership", "Adventure Rock membership"],
];
const PILLARS = [["CREATE", Palette], ["EXPLORE", Compass], ["MOVE", Activity], ["DISCOVER", Search], ["BELONG", HeartHandshake]];

export default function JoinList() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", mobile: "", child_age: "", interests: [] });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const utm = useRef({});
  const formRef = useRef(null);

  useEffect(() => {
    document.title = "Join the Adventure Rock Mailing List";
    const p = new URLSearchParams(window.location.search);
    utm.current = {
      utm_source: p.get("utm_source") || "", utm_campaign: p.get("utm_campaign") || "",
      utm_medium: p.get("utm_medium") || "", fbclid: p.get("fbclid") || "",
      referrer: document.referrer || "",
    };
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleInterest = (k) => setForm((f) => ({ ...f, interests: f.interests.includes(k) ? f.interests.filter((x) => x !== k) : [...f.interests, k] }));
  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email) { toast.error("Please add your name and email."); return; }
    setBusy(true);
    try {
      await api.post("/mailing-list", { ...form, ...utm.current });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-100 via-cyan-50 to-white flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-cyan-100 p-8 text-center" data-testid="join-success">
          <div className="w-16 h-16 rounded-2xl bg-turquoise/15 flex items-center justify-center mx-auto mb-4"><MailCheck className="w-8 h-8 text-turquoise" /></div>
          <h1 className="font-display font-black text-3xl text-navy">YOU'RE IN. 🧭</h1>
          <p className="text-slate-600 mt-3">You're officially on the Adventure Rock mailing list.</p>
          <p className="text-slate-600 mt-2">We'll let you know when pre-registration opens.</p>
          <p className="text-slate-500 text-sm mt-4">Keep an eye on your inbox — your next Adventure Rock update is coming soon.</p>
          <Link to="/" className="inline-block mt-6 text-turquoise font-bold text-sm" data-testid="join-home-link">Explore Adventure Rock →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 via-cyan-50 to-white text-navy">
      <div className="max-w-lg mx-auto px-5 py-6">
        <div className="flex justify-center mb-4">
          <Link to="/"><img src={LOGO} alt="Adventure Rock Bermuda" className="h-14 w-14 object-contain" /></Link>
        </div>

        <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-white" data-testid="join-hero">
          <img src={IMG.joinFlyer} alt="Adventure Rock Kids — Want to be the first to know?" fetchpriority="high" decoding="async" className="w-full block" />
        </div>

        <div className="text-center mt-6">
          <h1 className="font-display font-black text-4xl sm:text-5xl text-navy leading-tight">WANT TO BE THE <span className="text-gold">FIRST</span> TO KNOW?</h1>
          <p className="text-lg font-semibold text-ocean mt-2">Pre-registration is opening soon.</p>
          <p className="text-slate-600 mt-3">Join the Adventure Rock mailing list and be among the first families notified when registration goes live.</p>
          <Button onClick={scrollToForm} className="mt-5 bg-gold hover:bg-amber-400 text-navy font-black text-lg rounded-full px-8 py-6 shadow-lg" data-testid="join-cta-top">
            JOIN THE MAILING LIST <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
          <p className="text-xs text-slate-500 mt-3 flex items-center justify-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-turquoise" /> No spam. Just Adventure Rock news, registration updates and important announcements.</p>
          <Link to="/pricing" className="inline-block mt-3 text-turquoise font-semibold text-sm underline underline-offset-4" data-testid="join-pricing-link">See our pricing →</Link>
        </div>

        <div className="mt-8 bg-navy rounded-3xl p-6 text-center">
          <p className="text-white/80 text-sm">Adventure Rock Bermuda is a new after-school experience where kids can:</p>
          <div className="flex flex-wrap justify-center gap-4 mt-4">
            {PILLARS.map(([label, Icon]) => (
              <div key={label} className="flex flex-col items-center gap-1 w-16" data-testid={`pillar-${label.toLowerCase()}`}>
                <div className="w-11 h-11 rounded-xl bg-turquoise/15 flex items-center justify-center"><Icon className="w-5 h-5 text-turquoise" /></div>
                <span className="text-[11px] font-display font-bold text-gold tracking-widest">{label}</span>
              </div>
            ))}
          </div>
        </div>

        <form ref={formRef} onSubmit={submit} className="mt-8 bg-white rounded-3xl shadow-xl border border-cyan-100 p-6 space-y-4 scroll-mt-6" data-testid="join-form">
          <h2 className="font-display font-extrabold text-2xl text-navy text-center">Join the list</h2>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Parent/Guardian First Name *</Label><Input value={form.first_name} onChange={set("first_name")} required className="rounded-xl" data-testid="join-first-name" /></div>
            <div><Label className="text-xs">Parent/Guardian Last Name *</Label><Input value={form.last_name} onChange={set("last_name")} required className="rounded-xl" data-testid="join-last-name" /></div>
          </div>
          <div><Label className="text-xs">Email Address *</Label><Input type="email" value={form.email} onChange={set("email")} required className="rounded-xl" data-testid="join-email" /></div>
          <div><Label className="text-xs">Mobile Number (optional)</Label><Input type="tel" value={form.mobile} onChange={set("mobile")} className="rounded-xl" data-testid="join-mobile" /></div>

          <div>
            <Label className="text-xs">How old is your Explorer?</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {AGES.map(([k, l]) => (
                <button type="button" key={k} onClick={() => setForm({ ...form, child_age: k })} data-testid={`join-age-${k}`}
                  className={`rounded-xl border-2 px-3 py-2.5 text-sm font-semibold transition-colors ${form.child_age === k ? "border-turquoise bg-turquoise/10 text-navy" : "border-slate-200 text-slate-500 hover:border-turquoise/50"}`}>{l}</button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">What are you interested in? (optional)</Label>
            <div className="grid gap-2 mt-1">
              {INTERESTS.map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer bg-slate-50 rounded-xl px-3 py-2">
                  <Checkbox checked={form.interests.includes(k)} onCheckedChange={() => toggleInterest(k)} data-testid={`join-interest-${k}`} /> {l}
                </label>
              ))}
            </div>
          </div>

          <Button type="submit" disabled={busy} className="w-full bg-gold hover:bg-amber-400 text-navy font-black text-lg rounded-full py-6 shadow-lg" data-testid="join-submit">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "JOIN THE LIST"}
          </Button>
          <p className="text-xs text-center text-slate-500 flex items-center justify-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-turquoise" /> No spam. Just Adventure Rock news and registration updates.</p>
        </form>

        <p className="text-center text-xs text-slate-400 mt-6">Adventure Rock Bermuda · After school, the adventure begins.</p>
      </div>
    </div>
  );
}
